import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, FileText } from 'lucide-react';
import { fetchDocumentsList } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { fieldControlClass } from '../components/ui/forms';

// Documents directory — unmodified appGetDocuments(query). Previously only
// reachable via a job's own detail view; this is the standalone list the
// current HTML UI already has.
export function DocumentsPage() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['documents', search], queryFn: () => fetchDocumentsList(search) });

  return (
    <div className="space-y-4">
      <PageHeader eyebrow="Records" title="Documents" />
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input className={`${fieldControlClass} pl-9`} placeholder="Search documents…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {query.isLoading && <LoadingState label="Loading documents…" />}
      {query.isError && (
        <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load documents.'} onRetry={() => query.refetch()} />
      )}
      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load documents.'} onRetry={() => query.refetch()} />}
      {query.data?.ok && (query.data.documents?.length ?? 0) === 0 && <EmptyState>No documents found.</EmptyState>}
      {query.data?.ok && (query.data.documents?.length ?? 0) > 0 && (
        <ul className="space-y-2">
          {query.data.documents!.map((doc, i) => (
            <li key={i} className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-3.5 shadow-card">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-gold-800 bg-gold-900/30 text-gold-200">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <a href={doc.url} target="_blank" rel="noreferrer" className="truncate font-semibold text-gold-300 hover:text-gold-200">
                    {doc.name}
                  </a>
                  <p className="truncate text-sm text-slate-400">
                    {doc.customer} — {doc.project}
                  </p>
                </div>
              </div>
              <Link to={`/jobs/${encodeURIComponent(doc.jobId)}`} className="shrink-0 text-xs font-semibold text-slate-400 hover:text-slate-200">
                View Job
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
